import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { money, periodLabel, shortDate } from "@/lib/format";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export default function PartnerPayoutDetail() {
  const { payoutId } = useParams();
  const [payouts, setPayouts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const payoutList = await api.get("/partner/payouts");
        setPayouts(payoutList.data);
        const p = payoutList.data.find(x => x.id === payoutId);
        if (p && p.period) {
          const ev = await api.get("/partner/events", { params: { period: p.period } });
          const ids = new Set(p.event_ids || []);
          setEvents(ev.data.filter(e => ids.has(e.id)));
        }
      } catch (_) {
        toast.error("Failed to load payout");
      } finally { setLoading(false); }
    }
    load();
  }, [payoutId]);

  const payout = payouts.find((p) => p.id === payoutId);

  if (loading) return <div className="text-slate-500 text-sm">Loading…</div>;
  if (!payout) return (
    <div className="text-center py-16">
      <div className="text-lg font-medium mb-2">Payout not found</div>
      <Link to="/portal" className="text-cyan-400 hover:text-cyan-300 text-sm">← Back to dashboard</Link>
    </div>
  );

  return (
    <div className="space-y-10">
      <div className="flex items-center justify-between">
        <Link to="/portal" data-testid="payout-back" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-50 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>
        <a
          data-testid="payout-detail-pdf"
          href={`${API_BASE}/partner/payouts/${payout.id}/report.pdf`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2.5 rounded-full text-sm font-medium transition-all"
        >
          <Download className="w-4 h-4" /> Download PDF
        </a>
      </div>

      <header>
        <div className="text-xs uppercase tracking-widest text-cyan-500 font-semibold mb-2">Payout</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium">{periodLabel(payout.period)}</h1>
        <div className="text-slate-400 mt-2 font-mono text-sm">Reference {payout.id.slice(0, 8)}</div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Tile label="Events" value={String(payout.event_count)} />
        <Tile label="Ex-GST" value={money(payout.amount_ex_gst)} />
        <Tile label="GST" value={money(payout.amount_gst)} />
        <Tile label="Total" value={money(payout.amount_total)} accent />
      </div>

      <div className="rounded-xl border border-slate-800/80 bg-slate-900 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Detail label="Status" value={<span className="capitalize">{payout.status.replace("_", " ")}</span>} />
          <Detail label="Paid on" value={payout.paid_at ? shortDate(payout.paid_at) : "—"} mono />
          <Detail label="Reference" value={payout.payment_reference || "—"} mono />
        </div>
        {payout.notes && (
          <div className="mt-6 pt-6 border-t border-slate-800">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Notes</div>
            <div className="text-sm">{payout.notes}</div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-800/80 bg-slate-900 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h3 className="font-display text-xl">Events included ({events.length})</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            <tr>
              <th className="text-left px-6 py-3">Paid</th>
              <th className="text-left px-6 py-3">Customer</th>
              <th className="text-left px-6 py-3">Product</th>
              <th className="text-right px-6 py-3">Amount ex-GST</th>
              <th className="text-right px-6 py-3">Rate</th>
              <th className="text-right px-6 py-3">Commission</th>
              <th className="text-right px-6 py-3">Invoice</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100" data-testid="payout-events-tbody">
            {events.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-500">No events in this payout.</td></tr>
            )}
            {events.map((e) => {
              const labels = Array.from(new Set(
                (e.line_breakdown?.lines || [])
                  .map((l) => l.matched_label)
                  .filter(Boolean)
              ));
              const productDisplay = labels.length > 0
                ? labels.join(" · ")
                : (e.plan_name || "—");
              return (
              <tr key={e.id} className={`${e.status === "voided" ? "opacity-50" : ""}`}>
                <td className="px-6 py-4 font-mono text-xs">{shortDate(e.paid_at)}</td>
                <td className="px-6 py-4">{e.customer_name || (e.customer_id ? `#${e.customer_id}` : "—")}</td>
                <td className="px-6 py-4">
                  {labels.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {labels.map((lbl) => (
                        <span
                          key={lbl}
                          data-testid={`product-tag-${e.id}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-xs font-medium"
                        >
                          {lbl}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-slate-500 text-sm">{productDisplay}</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right font-mono">{money(e.amount_paid_ex_gst)}</td>
                <td className="px-6 py-4 text-right font-mono text-slate-400">{e.rate_snapshot}%</td>
                <td className="px-6 py-4 text-right font-mono font-medium text-cyan-400">{money(e.commission_amount)}</td>
                <td className="px-6 py-4 text-right">
                  {e.invoice_id ? (
                    <a
                      href={`${API_BASE}/partner/invoices/${e.invoice_id}/pdf`}
                      target="_blank" rel="noreferrer"
                      data-testid={`invoice-pdf-${e.id}`}
                      title={`View invoice #${e.invoice_id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-cyan-300 transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5" /> PDF
                    </a>
                  ) : (
                    <span className="text-xs text-slate-600">—</span>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-800/80 bg-slate-900 p-6">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono text-2xl font-medium mt-2 ${accent ? "text-cyan-500" : "text-slate-50"}`}>{value}</div>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
